import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Check } from 'lucide-react';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

const IMAGES = {
  hero: "https://images.pexels.com/photos/3993308/pexels-photo-3993308.jpeg?auto=compress&cs=tinysrgb&w=1920",
};

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const serviceCategories = [
  { value: '', label: 'Select a category' },
  { value: 'women-braids', label: "Women's - Braids & Cornrows" },
  { value: 'women-wigs', label: "Women's - Wigs & Weaves" },
  { value: 'women-natural', label: "Women's - Natural Hair" },
  { value: 'women-treatment', label: "Women's - Treatments" },
  { value: 'women-occasion', label: "Women's - Bridal/Occasion" },
  { value: 'men-cuts', label: "Men's - Haircuts & Fades" },
  { value: 'men-beard', label: "Men's - Beard Grooming" },
  { value: 'men-locs', label: "Men's - Locs & Twists" },
  { value: 'kids', label: 'Kids Hair' },
  { value: 'consultation', label: 'Consultation' },
];

const services = {
  'women-braids': ['Knotless Braids', 'Box Braids', 'Cornrows', 'Micro Braids', 'Goddess Locs'],
  'women-wigs': ['Wig Installation', 'Wig Revamp', 'Wig Styling', 'Weave Installation'],
  'women-natural': ['Natural Hair Styling', 'Silk Press', 'Twist Out', 'Wash & Go'],
  'women-treatment': ['Hair Treatment', 'Relaxer', 'Hair Colouring', 'Deep Conditioning'],
  'women-occasion': ['Bridal Styling', 'Occasion Styling', 'Updo'],
  'men-cuts': ['Haircut / Fade', 'Skin Fade', 'Shape Up', 'Line Up'],
  'men-beard': ['Beard Trim', 'Beard Grooming', 'Haircut & Beard Combo'],
  'men-locs': ['Twists', 'Locs Maintenance', 'Locs Retwist', 'Cornrows'],
  'kids': ['Kids Girls Hair', 'Kids Boys Hair'],
  'consultation': ['Hair Consultation', 'Style Consultation'],
};

const timeSlots = [
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM', '6:00 PM'
];

export const BookAppointment = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    serviceCategory: '',
    specificService: '',
    preferredDate: null,
    preferredTime: '',
    stylistPreference: '',
    notes: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'serviceCategory' && { specificService: '' })
    }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, preferredDate: date }));
    setIsCalendarOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend only - just show success message
    setIsSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isSubmitted) {
    return (
      <main data-testid="book-page">
        {/* Page Header */}
        <section className="page-header min-h-[50vh]" style={{ backgroundImage: `url(${IMAGES.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-[#0A0A0A]" />
          <div className="container-custom relative z-10 text-center pt-24">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C9A227] flex items-center justify-center">
                <Check size={40} className="text-black" />
              </div>
              <h1 className="font-heading text-4xl md:text-5xl text-[#F5F5F5] mb-4">Booking <span className="italic">Received</span></h1>
            </motion.div>
          </div>
        </section>

        <section className="py-24 md:py-32 bg-[#0A0A0A]">
          <div className="container-custom">
            <motion.div 
              className="max-w-2xl mx-auto text-center success-message"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="booking-success-message"
            >
              <h2 className="font-heading text-3xl text-[#F5F5F5] mb-6">Thank You for Booking with UNATHI</h2>
              <p className="text-[#999] mb-8">
                Our team will contact you to confirm your appointment. We look forward to seeing you!
              </p>
              <div className="bg-[#0A0A0A] border border-[#C9A227]/30 p-6 text-left mb-8">
                <h3 className="font-heading text-xl text-[#C9A227] mb-4">Booking Summary</h3>
                <div className="space-y-2 text-[#999]">
                  <p><span className="text-[#F5F5F5]">Name:</span> {formData.fullName}</p>
                  <p><span className="text-[#F5F5F5]">Email:</span> {formData.email}</p>
                  <p><span className="text-[#F5F5F5]">Service:</span> {formData.specificService || formData.serviceCategory}</p>
                  <p><span className="text-[#F5F5F5]">Date:</span> {formData.preferredDate ? format(formData.preferredDate, 'PPP') : 'Not specified'}</p>
                  <p><span className="text-[#F5F5F5]">Time:</span> {formData.preferredTime || 'Not specified'}</p>
                </div>
              </div>
              <button 
                onClick={() => { setIsSubmitted(false); setFormData({ fullName: '', email: '', phone: '', serviceCategory: '', specificService: '', preferredDate: null, preferredTime: '', stylistPreference: '', notes: '' }); }}
                className="btn-primary"
                data-testid="book-another-btn"
              >
                Book Another Appointment
              </button>
            </motion.div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="book-page">
      {/* Page Header */}
      <section 
        className="page-header"
        style={{ 
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-[#0A0A0A]" />
        <div className="container-custom relative z-10 text-center pt-24">
          <motion.p 
            className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Schedule Your Visit
          </motion.p>
          <motion.h1 
            className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#F5F5F5]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Book an <span className="italic">Appointment</span>
          </motion.h1>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-24 md:py-32 bg-[#F9F9F9]" data-testid="booking-form-section">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl mx-auto"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.div className="text-center mb-12" variants={fadeInUp}>
              <p className="text-[#666] leading-relaxed">
                Fill out the form below and our team will contact you to confirm your appointment.
                For urgent bookings, please call or WhatsApp us directly.
              </p>
            </motion.div>

            <motion.form 
              onSubmit={handleSubmit}
              className="bg-white p-8 md:p-12 border border-neutral-100 shadow-lg"
              variants={fadeInUp}
              data-testid="booking-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                {/* Full Name */}
                <div>
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your full name"
                    data-testid="input-fullname"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="+44 7000 000000"
                    data-testid="input-phone"
                  />
                </div>

                {/* Service Category */}
                <div>
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Service Category *
                  </label>
                  <select
                    name="serviceCategory"
                    value={formData.serviceCategory}
                    onChange={handleInputChange}
                    required
                    className="form-input cursor-pointer"
                    data-testid="select-category"
                  >
                    {serviceCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Specific Service */}
                <div>
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Specific Service
                  </label>
                  <select
                    name="specificService"
                    value={formData.specificService}
                    onChange={handleInputChange}
                    className="form-input cursor-pointer"
                    disabled={!formData.serviceCategory}
                    data-testid="select-service"
                  >
                    <option value="">Select a service</option>
                    {formData.serviceCategory && services[formData.serviceCategory]?.map(service => (
                      <option key={service} value={service}>{service}</option>
                    ))}
                  </select>
                </div>

                {/* Preferred Date */}
                <div>
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Preferred Date *
                  </label>
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="form-input text-left flex items-center justify-between cursor-pointer"
                        data-testid="date-picker-trigger"
                      >
                        <span className={formData.preferredDate ? 'text-[#2E2E2E]' : 'text-[#999]'}>
                          {formData.preferredDate ? format(formData.preferredDate, 'PPP') : 'Select a date'}
                        </span>
                        <Calendar size={18} className="text-[#C9A227]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.preferredDate}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        initialFocus
                        data-testid="calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Preferred Time */}
                <div>
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Preferred Time *
                  </label>
                  <div className="relative">
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      required
                      className="form-input cursor-pointer"
                      data-testid="select-time"
                    >
                      <option value="">Select a time</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    <Clock size={18} className="absolute right-0 top-1/2 -translate-y-1/2 text-[#C9A227] pointer-events-none" />
                  </div>
                </div>

                {/* Stylist Preference */}
                <div className="md:col-span-2">
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Stylist Preference (Optional)
                  </label>
                  <input
                    type="text"
                    name="stylistPreference"
                    value={formData.stylistPreference}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Any stylist or specific name"
                    data-testid="input-stylist"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm text-[#2E2E2E] uppercase tracking-wider mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="form-input resize-none"
                    placeholder="Tell us about your hair length, style preferences, or any special requests..."
                    data-testid="input-notes"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                data-testid="submit-booking-btn"
              >
                Book Appointment
              </button>
            </motion.form>

            <motion.p 
              className="text-center text-[#666] text-sm mt-8"
              variants={fadeInUp}
            >
              By booking, you agree to our cancellation policy. 
              Please notify us at least 24 hours in advance if you need to reschedule.
            </motion.p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default BookAppointment;
